(() => {
  if (customElements.get('ag-cart-page')) return;

  const PROMOTION_CODES_ATTRIBUTE = 'AG Promotion Codes__';
  const PROMOTION_TITLES_ATTRIBUTE = 'AG Promotion Titles__';
  const FULL_EDIT_STORAGE_PREFIX = 'ag-full-edit:';
  const CART_NOTICE_STORAGE = 'ag-cart-notice';
  const PHOTO_SYNC_TYPES = new Set(['crystal-accessory', 'crystal-accessories', 'copy']);

  class ArtLaserCartPage extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('change', (event) => this.handleChange(event), { signal });
      this.addEventListener('input', (event) => this.handleInput(event), { signal });
      this.setupPhotoDrag(signal);

      const discountForm = this.querySelector('[data-discount-form]');
      if (discountForm) {
        discountForm.addEventListener('submit', (event) => this.applyDiscount(event), { signal });
      }

      const photoForm = this.querySelector('[data-photo-form]');
      if (photoForm) {
        photoForm.addEventListener('submit', (event) => this.savePhoto(event), { signal });
      }

      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('cancel', (event) => {
          if (dialog.matches('[data-photo-modal]') && this.busy) event.preventDefault();
          if (dialog.matches('[data-addon-remove-modal]')) {
            event.preventDefault();
            this.cancelAddonRemoval();
          }
        }, { signal });
        dialog.addEventListener('close', () => {
          if (dialog.matches('[data-photo-modal]')) this.resetPhotoEditor();
          if (dialog.matches('[data-addon-remove-modal]') && this.pendingAddonRemoval) {
            this.restoreAddonQuantitySelection();
          }
          this.syncModalState();
        }, { signal });
      });

      const notice = sessionStorage.getItem(CART_NOTICE_STORAGE);
      if (notice) {
        sessionStorage.removeItem(CART_NOTICE_STORAGE);
        queueMicrotask(() => this.showToast(notice));
      }

      this.renderCartPhotoComposites();
      queueMicrotask(() => this.reconcileConfigurationState());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resetPhotoEditor();
      this.syncModalState();
    }

    async handleClick(event) {
      const categoryToggle = event.target.closest('[data-addon-category-toggle]');
      if (categoryToggle) {
        event.preventDefault();
        this.toggleAddonCategory(categoryToggle);
        return;
      }

      const zoomButton = event.target.closest('[data-addon-zoom]');
      if (zoomButton) {
        event.preventDefault();
        this.openImageModal(zoomButton);
        return;
      }

      if (event.target.closest('[data-addon-modal-close]')) {
        event.preventDefault();
        this.closeDialog('[data-addon-modal]');
        return;
      }

      const addonModal = event.target.closest('[data-addon-modal]');
      if (addonModal && event.target === addonModal) {
        this.closeDialog('[data-addon-modal]');
        return;
      }

      if (event.target.closest('[data-addon-remove-cancel]')) {
        event.preventDefault();
        this.cancelAddonRemoval();
        return;
      }

      if (event.target.closest('[data-addon-remove-confirm]')) {
        event.preventDefault();
        await this.confirmAddonRemoval();
        return;
      }

      const addonRemoveModal = event.target.closest('[data-addon-remove-modal]');
      if (addonRemoveModal && event.target === addonRemoveModal) {
        this.cancelAddonRemoval();
        return;
      }

      const accessoryAdjustButton = event.target.closest('[data-accessory-adjust]');
      if (accessoryAdjustButton) {
        event.preventDefault();
        this.openAccessoryAdjustModal(accessoryAdjustButton);
        return;
      }

      const editPhotoButton = event.target.closest('[data-edit-photo]');
      if (editPhotoButton) {
        event.preventDefault();
        this.openPhotoModal(editPhotoButton);
        return;
      }

      if (event.target.closest('[data-photo-modal-close]')) {
        event.preventDefault();
        this.closeDialog('[data-photo-modal]');
        return;
      }

      const photoModal = event.target.closest('[data-photo-modal]');
      if (photoModal && event.target === photoModal) {
        this.closeDialog('[data-photo-modal]');
        return;
      }

      const openPromotionsButton = event.target.closest('[data-open-promotions]');
      if (openPromotionsButton) {
        event.preventDefault();
        this.openDialog('[data-promotions-modal]');
        return;
      }

      if (event.target.closest('[data-promotions-modal-close]')) {
        event.preventDefault();
        this.closeDialog('[data-promotions-modal]');
        return;
      }

      const promotionsModal = event.target.closest('[data-promotions-modal]');
      if (promotionsModal && event.target === promotionsModal) {
        this.closeDialog('[data-promotions-modal]');
        return;
      }

      const promotionButton = event.target.closest('[data-apply-promotion]');
      if (promotionButton) {
        event.preventDefault();
        await this.applyPromotion(
          promotionButton.dataset.promotionCode,
          promotionButton.dataset.promotionTitle,
          promotionButton
        );
        return;
      }

      if (event.target.closest('[data-remove-promotions]')) {
        event.preventDefault();
        await this.removePromotions();
        return;
      }

      if (event.target.closest('[data-conflict-keep]')) {
        event.preventDefault();
        this.pendingConflict = null;
        this.closeDialog('[data-conflict-modal]');
        return;
      }

      if (event.target.closest('[data-conflict-apply]')) {
        event.preventDefault();
        await this.replacePromotion();
        return;
      }

      const addonButton = event.target.closest('[data-addon-add]');
      if (addonButton) {
        event.preventDefault();
        const addonType = this.normalize(addonButton.dataset.itemType);
        if (addonType === 'crystal-accessory' || addonType === 'crystal-accessories') {
          await this.openAccessoryAddModal(addonButton);
        } else {
          await this.addAddon(addonButton);
        }
        return;
      }

      const copyButton = event.target.closest('[data-add-copy]');
      if (copyButton) {
        event.preventDefault();
        await this.addCopy(copyButton);
        return;
      }

      const fullEditLink = event.target.closest('[data-edit-full]');
      if (fullEditLink) {
        event.preventDefault();
        await this.openFullConfiguration(fullEditLink);
        return;
      }

      const quantityButton = event.target.closest('[data-quantity-change]');
      if (quantityButton) {
        event.preventDefault();
        const quantity = Math.max(0, Number.parseInt(quantityButton.dataset.nextQuantity || '0', 10));
        const configuration = quantityButton.closest('[data-configuration]');
        if (configuration && quantityButton.dataset.lineKey === configuration.dataset.mainLineKey) {
          await this.updateConfigurationQuantity(configuration, quantity);
        } else {
          await this.updateLine(quantityButton.dataset.lineKey, quantity);
        }
        return;
      }

      const addonRemoveButton = event.target.closest('[data-addon-remove]');
      if (addonRemoveButton) {
        event.preventDefault();
        const addonRow = addonRemoveButton.closest('.ag-cart__addon--selected');
        this.openAddonRemoval({
          lineKey: addonRemoveButton.dataset.lineKey,
          title: addonRemoveButton.dataset.lineTitle,
          previousQuantity: addonRemoveButton.dataset.currentQuantity,
          select: addonRow?.querySelector('[data-addon-quantity]') || null
        });
        return;
      }

      const removeLineButton = event.target.closest('[data-remove-line]');
      if (removeLineButton) {
        event.preventDefault();
        await this.updateLine(removeLineButton.dataset.lineKey, 0);
        return;
      }

      const removeGroupButton = event.target.closest('[data-remove-group]');
      if (removeGroupButton) {
        event.preventDefault();
        const configuration = removeGroupButton.closest('[data-configuration]');
        const configurationId = configuration?.dataset.configuration || '';
        await this.removeConfigurationById(configurationId);
      }
    }

    async handleChange(event) {
      const addonQuantity = event.target.closest('[data-addon-quantity]');
      if (addonQuantity) {
        await this.changeAddonQuantity(addonQuantity);
        return;
      }

      const quantityInput = event.target.closest('[data-quantity-input]');
      if (quantityInput) {
        const quantity = Math.max(0, Number.parseInt(quantityInput.value || '0', 10));
        const configuration = quantityInput.closest('[data-configuration]');
        if (configuration && quantityInput.dataset.lineKey === configuration.dataset.mainLineKey) {
          await this.updateConfigurationQuantity(configuration, quantity);
        } else {
          await this.updateLine(quantityInput.dataset.lineKey, quantity);
        }
        return;
      }

      const photoInput = event.target.closest('[data-photo-input]');
      if (photoInput) await this.previewPhoto(photoInput.files?.[0]);
    }

    handleInput(event) {
      if (!this.photoEditorState) return;
      if (event.target.matches('[data-photo-fit-control]')) this.photoEditorState.fit = event.target.value === 'fit' ? 'fit' : 'fill';
      else if (event.target.matches('[data-photo-zoom-control]')) this.photoEditorState.zoom = Number(event.target.value || 100);
      else if (event.target.matches('[data-photo-rotation-control]')) this.photoEditorState.rotation = Number(event.target.value || 0);
      else return;
      this.renderPhotoEditor();
    }

    normalize(value) {
      return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    configurationIdFor(item) {
      return item?.properties?.['_Configuration ID'] || item?.properties?.['_AuraGleam customization ID'] || '';
    }

    createToken(prefix) {
      if (globalThis.crypto?.randomUUID) return prefix + '-' + globalThis.crypto.randomUUID();
      return prefix + '-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    }

    openDialog(selector) {
      const dialog = this.querySelector(selector);
      if (!dialog) return;
      document.body.classList.add('ag-cart-modal-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closeDialog(selector) {
      const dialog = this.querySelector(selector);
      if (!dialog) return;
      const isPhotoModal = dialog.matches('[data-photo-modal]');
      if (isPhotoModal && this.busy) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        if (isPhotoModal) this.resetPhotoEditor();
      }
      this.syncModalState();
    }

    syncModalState() {
      const anyOpen = [...this.querySelectorAll('dialog')].some((dialog) => dialog.open || dialog.hasAttribute('open'));
      document.body.classList.toggle('ag-cart-modal-open', anyOpen);
    }

    async changeAddonQuantity(select) {
      const previousQuantity = Math.max(1, Number.parseInt(select.dataset.currentQuantity || '1', 10));
      const nextQuantity = Math.max(0, Number.parseInt(select.value || '0', 10));
      if (nextQuantity === previousQuantity) return;
      if (this.busy) {
        select.value = String(previousQuantity);
        return;
      }
      if (nextQuantity === 0) {
        this.openAddonRemoval({
          lineKey: select.dataset.lineKey,
          title: select.dataset.lineTitle,
          previousQuantity,
          select
        });
        return;
      }
      const updated = await this.updateLine(select.dataset.lineKey, nextQuantity);
      if (!updated && select.isConnected) select.value = String(previousQuantity);
    }

    openAddonRemoval({ lineKey, title, previousQuantity, select }) {
      if (!lineKey || this.busy) return;
      const modal = this.querySelector('[data-addon-remove-modal]');
      if (!modal) return;
      this.pendingAddonRemoval = {
        lineKey,
        select,
        previousQuantity: Math.max(1, Number.parseInt(String(previousQuantity || '1'), 10))
      };
      const titleNode = modal.querySelector('[data-addon-remove-title]');
      if (titleNode) titleNode.textContent = title || 'this add-on';
      this.openDialog('[data-addon-remove-modal]');
    }

    restoreAddonQuantitySelection() {
      const pending = this.pendingAddonRemoval;
      if (pending?.select?.isConnected) pending.select.value = String(pending.previousQuantity);
      this.pendingAddonRemoval = null;
    }

    cancelAddonRemoval() {
      this.restoreAddonQuantitySelection();
      this.closeDialog('[data-addon-remove-modal]');
    }

    async confirmAddonRemoval() {
      if (!this.pendingAddonRemoval || this.busy) return;
      const pending = this.pendingAddonRemoval;
      this.pendingAddonRemoval = null;
      this.closeDialog('[data-addon-remove-modal]');
      const updated = await this.updateLine(pending.lineKey, 0);
      if (!updated && pending.select?.isConnected) pending.select.value = String(pending.previousQuantity);
    }

    resetPhotoEditor() {
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
      this.photoEditorContext = null;
      this.photoEditorState = null;
      this.photoDrag = null;
    }

    openImageModal(button) {
      const modal = this.querySelector('[data-addon-modal]');
      const image = modal?.querySelector('[data-addon-modal-image]');
      const composite = modal?.querySelector('[data-addon-modal-composite]');
      const title = modal?.querySelector('[data-addon-modal-title]');
      const imageUrl = button.dataset.imageUrl;
      const imageAlt = button.dataset.imageAlt || 'Product image';
      const canComposite = Boolean(
        button.dataset.compositeBaseUrl &&
        button.dataset.compositeMaskUrl &&
        button.dataset.compositeOverlayUrl &&
        button.dataset.compositePhotoUrl
      );
      if (!modal || !image || (!imageUrl && !canComposite)) return;
      if (canComposite && composite) {
        const base = composite.querySelector('[data-addon-modal-base]');
        const windowNode = composite.querySelector('[data-addon-modal-window]');
        const photo = composite.querySelector('[data-addon-modal-photo]');
        const overlay = composite.querySelector('[data-addon-modal-overlay]');
        base.src = button.dataset.compositeBaseUrl;
        photo.src = button.dataset.compositePhotoUrl;
        photo.alt = imageAlt;
        overlay.src = button.dataset.compositeOverlayUrl;
        windowNode.style.setProperty('--ag-cart-photo-mask', `url("${button.dataset.compositeMaskUrl}")`);
        composite.dataset.photoTransform = button.dataset.compositeTransform || '';
        composite.dataset.photoFit = button.dataset.compositeFit || 'Fill shape';
        composite.hidden = false;
        image.hidden = true;
        this.applyPhotoCompositeTransform(composite);
      } else {
        image.src = imageUrl;
        image.alt = imageAlt;
        image.hidden = false;
        if (composite) composite.hidden = true;
      }
      if (title) title.textContent = imageAlt;
      this.openDialog('[data-addon-modal]');
    }

    accessoryAssetMap() {
      return {
        'accessory-necklace-heart': {
          baseUrl: this.dataset.accessoryNecklaceHeartBaseUrl || '',
          maskUrl: this.dataset.accessoryNecklaceHeartMaskUrl || '',
          overlayUrl: this.dataset.accessoryNecklaceHeartOverlayUrl || '',
        },
        'accessory-necklace-rectangle': {
          baseUrl: this.dataset.accessoryNecklaceRectangleBaseUrl || '',
          maskUrl: this.dataset.accessoryNecklaceRectangleMaskUrl || '',
          overlayUrl: this.dataset.accessoryNecklaceRectangleOverlayUrl || '',
        },
        'accessory-keychain-heart': {
          baseUrl: this.dataset.accessoryKeychainHeartBaseUrl || '',
          maskUrl: this.dataset.accessoryKeychainHeartMaskUrl || '',
          overlayUrl: this.dataset.accessoryKeychainHeartOverlayUrl || '',
        },
        'accessory-keychain-rectangle': {
          baseUrl: this.dataset.accessoryKeychainRectangleBaseUrl || '',
          maskUrl: this.dataset.accessoryKeychainRectangleMaskUrl || '',
          overlayUrl: this.dataset.accessoryKeychainRectangleOverlayUrl || '',
        },
      };
    }

    accessoryProfileForItem(item) {
      const savedProfile = this.normalize(item?.properties?.['Personalizer profile']);
      if (savedProfile.startsWith('accessory-')) return savedProfile;
      const handle = this.normalize(item?.handle || String(item?.url || '').split('/products/')[1]?.split(/[?#]/)[0] || '');
      return {
        'necklace-heart-3d': 'accessory-necklace-heart',
        'necklace-rectang-3d': 'accessory-necklace-rectangle',
        'keychain-heart-3d': 'accessory-keychain-heart',
        'keychain-rectangle-3d': 'accessory-keychain-rectangle',
      }[handle] || '';
    }

    isPhotoSyncItem(item) {
      const itemType = this.normalize(item?.properties?.['_Item type']);
      return PHOTO_SYNC_TYPES.has(itemType) || this.normalize(item?.product_type) === 'crystal-accessories';
    }

    isAccessoryItem(item) {
      const itemType = this.normalize(item?.properties?.['_Item type']);
      return itemType === 'crystal-accessory' ||
        itemType === 'crystal-accessories' ||
        this.normalize(item?.product_type) === 'crystal-accessories';
    }

    isCustomAccessoryPhoto(item) {
      if (!this.isAccessoryItem(item)) return false;
      const override = this.normalize(item?.properties?.['_Accessory photo override']);
      return this.normalize(item?.properties?.['_Accessory photo mode']) === 'custom' ||
        ['true', 'yes', '1', 'on'].includes(override);
    }

    photoUrlForItem(item) {
      return item?.properties?.['Original photo'] || item?.properties?.['_Original photo'] || '';
    }

    setPhotoModalCopy(eyebrow, title, description, submitLabel) {
      const modal = this.querySelector('[data-photo-modal]');
      if (!modal) return;
      const eyebrowNode = modal.querySelector('[data-photo-modal-eyebrow]');
      const titleNode = modal.querySelector('[data-photo-modal-title]');
      const descriptionNode = modal.querySelector('[data-photo-modal-description]');
      const submitNode = modal.querySelector('[data-photo-submit]');
      if (eyebrowNode) eyebrowNode.textContent = eyebrow;
      if (titleNode) titleNode.textContent = title;
      if (descriptionNode) descriptionNode.textContent = description;
      if (submitNode) submitNode.textContent = submitLabel;
    }

    async openAccessoryAddModal(button) {
      if (this.busy || button.disabled) return;
      const variantId = Number.parseInt(button.dataset.variantId || '', 10);
      const parentLineKey = button.dataset.parentLineKey || '';
      if (!variantId || !parentLineKey) return;

      this.setBusy(true);
      this.showError('');
      button.setAttribute('aria-busy', 'true');
      try {
        const cart = await this.fetchCart();
        const requestedConfigurationId = button.dataset.configurationId || '';
        const parent = cart.items.find((item) => item.key === parentLineKey) ||
          cart.items.find((item) =>
            requestedConfigurationId &&
            item.properties?.['_Configuration ID'] === requestedConfigurationId &&
            !item.properties?.['_Item type']
          );
        if (!parent) throw new Error('The main crystal was not found.');

        const profile = button.dataset.photoProfile || '';
        const assets = this.accessoryAssetMap()[profile] || {};
        const opened = this.openAccessoryPhotoModal({
          type: 'accessory-add',
          variantId,
          parentLineKey: parent.key,
          configurationId: this.configurationIdFor(parent) || requestedConfigurationId || parent.key,
          productTitle: button.dataset.productTitle || 'Crystal accessory',
          profile,
          baseUrl: assets.baseUrl || '',
          maskUrl: assets.maskUrl || '',
          overlayUrl: assets.overlayUrl || '',
          originalUrl: this.photoUrlForItem(parent),
          adjustedUrl: parent.properties?.['Adjusted preview'] || parent.properties?.['_Adjusted preview'] || '',
          transform: parent.properties?.['Photo transformation'] || '',
          fit: parent.properties?.['Photo fit'] || '',
          addonData: {
            variantId: button.dataset.variantId || '',
            parentLineKey: parent.key,
            configurationId: this.configurationIdFor(parent) || requestedConfigurationId || parent.key,
            itemType: button.dataset.itemType || 'Crystal accessory',
            configuredWith: button.dataset.configuredWith || parent.product_title || '',
            samePhoto: button.dataset.samePhoto || 'true',
            productHandle: button.dataset.productHandle || '',
            photoProfile: profile
          }
        });
        if (!opened) throw new Error('This accessory photo outline is not configured yet.');
      } catch (error) {
        this.showError(error.message || 'We could not prepare this accessory. Please try again.');
      } finally {
        button.removeAttribute('aria-busy');
        this.setBusy(false);
      }
    }

    openAccessoryAdjustModal(button) {
      return this.openAccessoryPhotoModal({
        type: 'accessory-adjust',
        targetLineKey: button.dataset.lineKey || '',
        parentLineKey: button.dataset.parentLineKey || '',
        configurationId: button.dataset.configurationId || '',
        productTitle: button.dataset.title || 'Crystal accessory',
        profile: button.dataset.photoProfile || '',
        baseUrl: button.dataset.photoBaseUrl || '',
        maskUrl: button.dataset.photoMaskUrl || '',
        overlayUrl: button.dataset.photoOverlayUrl || '',
        originalUrl: button.dataset.photoOriginalUrl || '',
        adjustedUrl: button.dataset.photoAdjustedUrl || '',
        transform: button.dataset.photoTransform || '',
        fit: button.dataset.photoFit || '',
        currentPhotoMode: button.dataset.accessoryPhotoMode || 'synced'
      });
    }

    openAccessoryPhotoModal(context) {
      const modal = this.querySelector('[data-photo-modal]');
      if (!modal) return;
      const assets = this.accessoryAssetMap()[context.profile] || {};
      context.baseUrl = context.baseUrl || assets.baseUrl || '';
      context.maskUrl = context.maskUrl || assets.maskUrl || '';
      context.overlayUrl = context.overlayUrl || assets.overlayUrl || '';
      if (!context.profile || !context.baseUrl || !context.maskUrl || !context.overlayUrl) {
        this.showError('This accessory photo outline is not configured yet.');
        return false;
      }
      this.photoEditorContext = context;

      modal.querySelector('[data-photo-line-key]').value = context.targetLineKey || context.parentLineKey || '';
      modal.querySelector('[data-photo-configuration-id]').value = context.configurationId || '';
      const error = modal.querySelector('[data-photo-error]');
      if (error) error.textContent = '';
      const input = modal.querySelector('[data-photo-input]');
      if (input) input.value = '';

      const transform = this.parsePhotoTransform(context.transform || '');
      this.photoEditorState = {
        hasComposite: Boolean(context.baseUrl && context.maskUrl && context.overlayUrl),
        profile: context.profile || 'default',
        mode: 'fixed',
        layout: 'default',
        baseUrl: context.baseUrl,
        maskUrl: context.maskUrl,
        overlayUrl: context.overlayUrl,
        originalUrl: context.originalUrl || '',
        adjustedUrl: context.adjustedUrl || '',
        sourceUrl: context.originalUrl || context.adjustedUrl || '',
        fit: /(?:fit|show\s+full)/i.test(context.fit || '') ? 'fit' : transform.fit,
        zoom: transform.zoom,
        rotation: transform.rotation,
        x: transform.x,
        y: transform.y,
        engravingText: '',
        engravingFont: 'Georgia',
        engravingSize: 32,
        engravingX: 0,
        engravingY: 0,
        accessoryAssets: this.accessoryAssetMap(),
        file: null
      };
      this.photoEditorState.initialTransformation = this.photoTransformationValue(this.photoEditorState);
      this.photoEditorState.initialLayout = this.photoEditorState.layout;

      const isAdd = context.type === 'accessory-add';
      this.setPhotoModalCopy(
        'Adjust photo',
        'Update ' + context.productTitle,
        isAdd
          ? 'The main crystal photo is ready in this accessory. Adjust it or choose a different photo before applying.'
          : 'Adjust this accessory photo, or choose a different one. This does not change the main crystal.',
        'Apply'
      );
      this.renderPhotoEditor();
      const panel = modal.querySelector('.ag-cart__photo-modal-panel');
      if (panel) panel.scrollTop = 0;
      this.openDialog('[data-photo-modal]');
      requestAnimationFrame(() => { if (panel) panel.scrollTop = 0; });
      return true;
    }

    openPhotoModal(button) {
      const modal = this.querySelector('[data-photo-modal]');
      if (!modal) return;
      this.photoEditorContext = { type: 'main' };
      this.setPhotoModalCopy(
        'Edit photo',
        'Update ' + (button.dataset.title || 'this configuration'),
        'Adjust the current photo, or choose a different one. The crystal shape, text, options and existing add-ons stay attached to this configuration.',
        'Save photo changes'
      );
      modal.querySelector('[data-photo-line-key]').value = button.dataset.lineKey || '';
      modal.querySelector('[data-photo-configuration-id]').value = button.dataset.configurationId || '';
      const title = modal.querySelector('[data-photo-modal-title]');
      if (title) title.textContent = 'Update ' + (button.dataset.title || 'this configuration');
      const error = modal.querySelector('[data-photo-error]');
      if (error) error.textContent = '';
      const input = modal.querySelector('[data-photo-input]');
      if (input) input.value = '';
      const transform = this.parsePhotoTransform(button.dataset.photoTransform || '');
      this.photoEditorState = {
        hasComposite: button.dataset.photoHasComposite === 'true',
        profile: button.dataset.photoProfile || 'default',
        mode: button.dataset.photoMode || 'fixed',
        layout: button.dataset.photoLayout || 'default',
        baseUrl: button.dataset.photoBaseUrl || '',
        maskUrl: button.dataset.photoMaskUrl || '',
        overlayUrl: button.dataset.photoOverlayUrl || '',
        originalUrl: button.dataset.photoOriginalUrl || '',
        adjustedUrl: button.dataset.photoAdjustedUrl || '',
        sourceUrl: button.dataset.photoOriginalUrl || '',
        fit: /fit/i.test(button.dataset.photoFit || '') ? 'fit' : transform.fit,
        zoom: transform.zoom,
        rotation: transform.rotation,
        x: transform.x,
        y: transform.y,
        engravingText: button.dataset.photoEngravingText || '',
        engravingFont: button.dataset.photoEngravingFont || 'Georgia',
        engravingSize: Number(button.dataset.photoEngravingSize || 32),
        engravingX: Number(button.dataset.photoEngravingX || 0),
        engravingY: Number(button.dataset.photoEngravingY || 0),
        accessoryAssets: this.accessoryAssetMap(),
        file: null,
      };
      this.photoEditorState.initialTransformation = this.photoTransformationValue(this.photoEditorState);
      this.photoEditorState.initialLayout = this.photoEditorState.layout;
      this.renderPhotoEditor();
      const panel = modal.querySelector('.ag-cart__photo-modal-panel');
      if (panel) panel.scrollTop = 0;
      this.openDialog('[data-photo-modal]');
      requestAnimationFrame(() => { if (panel) panel.scrollTop = 0; });
    }

    parsePhotoTransform(value) {
      const source = String(value || '');
      const read = (expression, fallback) => Number(source.match(expression)?.[1] ?? fallback);
      return {
        fit: /(?:mode\s+(?:fit|show\s+full)|fit\s+entire|show\s+full)/i.test(source) ? 'fit' : 'fill',
        zoom: read(/zoom\s*(-?\d+(?:\.\d+)?)%?/i, 100),
        rotation: read(/rotation\s*(-?\d+(?:\.\d+)?)deg/i, 0),
        x: read(/position\s+x\s*(-?\d+(?:\.\d+)?)%/i, 0),
        y: read(/(?:position\s+x[^,]*,\s*)?y\s*(-?\d+(?:\.\d+)?)%/i, 0),
      };
    }

    applyPhotoCompositeTransform(node) {
      const transform = this.parsePhotoTransform(node?.dataset.photoTransform || '');
      const image = node?.querySelector('.ag-cart__composite-photo');
      if (!image) return;
      image.style.setProperty('--ag-photo-fit', /(?:fit|show\s+full)/i.test(node.dataset.photoFit || '') ? 'contain' : 'cover');
      image.style.setProperty('--ag-photo-zoom', String(this.clamp(transform.zoom, 100, 300) / 100));
      image.style.setProperty('--ag-photo-rotation', `${this.clamp(transform.rotation, -180, 180)}deg`);
      image.style.setProperty('--ag-photo-x', String(this.clamp(transform.x, -60, 60)));
      image.style.setProperty('--ag-photo-y', String(this.clamp(transform.y, -60, 60)));
    }

    renderCartPhotoComposites() {
      this.querySelectorAll('[data-cart-photo-composite]').forEach((node) => this.applyPhotoCompositeTransform(node));
    }

    clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, Number(value) || 0));
    }

    photoAssetForLayout(url, layout) {
      if (!url || !['portrait', 'landscape'].includes(layout)) return url;
      return url.replace(/-(portrait|landscape)-/i, `-${layout}-`);
    }

    setPhotoEditorLayout(layout) {
      const state = this.photoEditorState;
      if (!state || state.mode !== 'orientation' || !['portrait', 'landscape'].includes(layout)) return;
      state.layout = layout;
      state.baseUrl = this.photoAssetForLayout(state.baseUrl, layout);
      state.maskUrl = this.photoAssetForLayout(state.maskUrl, layout);
      state.overlayUrl = this.photoAssetForLayout(state.overlayUrl, layout);
    }

    renderPhotoEditor() {
      const state = this.photoEditorState;
      const modal = this.querySelector('[data-photo-modal]');
      if (!state || !modal) return;
      const composite = modal.querySelector('[data-photo-editor-composite]');
      const base = modal.querySelector('[data-photo-editor-base]');
      const windowNode = modal.querySelector('[data-photo-editor-window]');
      const image = modal.querySelector('[data-photo-editor-image]');
      const overlay = modal.querySelector('[data-photo-editor-overlay]');
      const engraving = modal.querySelector('[data-photo-editor-engraving]');
      const raw = modal.querySelector('[data-photo-preview]');
      const rawImage = raw?.querySelector('img');
      const empty = modal.querySelector('[data-photo-editor-empty]');
      const controls = modal.querySelector('[data-photo-controls]');
      const fitControl = modal.querySelector('[data-photo-fit-control]');
      const zoomControl = modal.querySelector('[data-photo-zoom-control]');
      const rotationControl = modal.querySelector('[data-photo-rotation-control]');
      const canComposite = state.hasComposite && state.sourceUrl && state.baseUrl && state.maskUrl && state.overlayUrl;

      if (canComposite) {
        base.src = state.baseUrl;
        overlay.src = state.overlayUrl;
        image.src = state.sourceUrl;
        windowNode.style.setProperty('--ag-cart-photo-mask', `url("${state.maskUrl}")`);
        image.style.setProperty('--ag-photo-fit', state.fit === 'fit' ? 'contain' : 'cover');
        image.style.setProperty('--ag-photo-zoom', String(this.clamp(state.zoom, 100, 300) / 100));
        image.style.setProperty('--ag-photo-rotation', `${this.clamp(state.rotation, -180, 180)}deg`);
        image.style.setProperty('--ag-photo-x', String(this.clamp(state.x, -60, 60)));
        image.style.setProperty('--ag-photo-y', String(this.clamp(state.y, -60, 60)));
        if (state.engravingText) {
          engraving.textContent = state.engravingText;
          engraving.style.fontFamily = state.engravingFont;
          engraving.style.fontSize = `${this.clamp(state.engravingSize, 14, 72) * 0.42}px`;
          engraving.style.left = `${50 + this.clamp(state.engravingX, -38, 38)}%`;
          engraving.style.top = `${50 + this.clamp(state.engravingY, -38, 38)}%`;
          engraving.hidden = false;
        } else {
          engraving.hidden = true;
        }
        composite.hidden = false;
        if (raw) raw.hidden = true;
        if (empty) empty.hidden = true;
        if (controls) controls.hidden = false;
      } else if (state.adjustedUrl) {
        if (rawImage) rawImage.src = state.adjustedUrl;
        if (raw) raw.hidden = false;
        if (composite) composite.hidden = true;
        if (empty) empty.hidden = true;
        if (controls) controls.hidden = true;
      } else {
        if (composite) composite.hidden = true;
        if (raw) raw.hidden = true;
        if (empty) empty.hidden = false;
        if (controls) controls.hidden = true;
      }
      if (fitControl) fitControl.value = state.fit;
      if (zoomControl) zoomControl.value = String(this.clamp(state.zoom, 100, 300));
      if (rotationControl) rotationControl.value = String(this.clamp(state.rotation, -180, 180));
    }

    setupPhotoDrag(signal) {
      this.addEventListener('pointerdown', (event) => {
        const windowNode = event.target.closest('[data-photo-editor-window]');
        if (!windowNode || !this.photoEditorState?.sourceUrl) return;
        const rect = windowNode.getBoundingClientRect();
        this.photoDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          originX: this.photoEditorState.x,
          originY: this.photoEditorState.y,
          width: Math.max(1, rect.width),
          height: Math.max(1, rect.height),
        };
        windowNode.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      }, { signal });
      this.addEventListener('pointermove', (event) => {
        if (!this.photoDrag || event.pointerId !== this.photoDrag.pointerId || !this.photoEditorState) return;
        this.photoEditorState.x = this.clamp(this.photoDrag.originX + ((event.clientX - this.photoDrag.startX) / this.photoDrag.width) * 100, -60, 60);
        this.photoEditorState.y = this.clamp(this.photoDrag.originY + ((event.clientY - this.photoDrag.startY) / this.photoDrag.height) * 100, -60, 60);
        this.renderPhotoEditor();
      }, { signal });
      const finishDrag = (event) => {
        if (this.photoDrag && event.pointerId === this.photoDrag.pointerId) this.photoDrag = null;
      };
      this.addEventListener('pointerup', finishDrag, { signal });
      this.addEventListener('pointercancel', finishDrag, { signal });
    }

    imageDimensions(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
        image.onerror = reject;
        image.src = src;
      });
    }

    async previewPhoto(file) {
      const state = this.photoEditorState;
      if (!state || !file) return;
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = URL.createObjectURL(file);
      state.file = file;
      state.sourceUrl = this.photoObjectUrl;
      state.originalUrl = '';
      state.adjustedUrl = '';
      state.fit = 'fill';
      state.zoom = 100;
      state.rotation = 0;
      state.x = 0;
      state.y = 0;
      if (state.mode === 'orientation') {
        try {
          const dimensions = await this.imageDimensions(this.photoObjectUrl);
          this.setPhotoEditorLayout(dimensions.width >= dimensions.height ? 'landscape' : 'portrait');
        } catch (error) {
          const errorNode = this.querySelector('[data-photo-error]');
          if (errorNode) errorNode.textContent = 'This image could not be previewed. Please choose a JPG, PNG or WEBP file.';
          return;
        }
      }
      this.renderPhotoEditor();
    }

    toggleAddonCategory(button) {
      const category = button.closest('[data-addon-category]');
      if (!category) return;
      const willExpand = button.getAttribute('aria-expanded') !== 'true';
      category.querySelectorAll('.ag-cart__addon--collapsed').forEach((item) => {
        item.hidden = !willExpand;
      });
      button.setAttribute('aria-expanded', String(willExpand));
      const label = button.querySelector('[data-addon-toggle-label]');
      if (label) label.textContent = willExpand ? button.dataset.collapseLabel : button.dataset.expandLabel;
    }

    async addAddon(button) {
      if (this.busy || button.disabled) return;
      const variantId = Number.parseInt(button.dataset.variantId || '', 10);
      let parentLineKey = button.dataset.parentLineKey;
      if (!variantId || !parentLineKey) return;
      this.setBusy(true);
      this.showError('');
      button.setAttribute('aria-busy', 'true');

      try {
        let currentCart = await this.fetchCart();
        let parentItem = currentCart.items.find((item) => item.key === parentLineKey);
        if (!parentItem) throw new Error('Parent cart item was not found');
        const normalizedParent = await this.ensureConfigurationId(parentItem);
        parentItem = normalizedParent.item;
        parentLineKey = parentItem.key;
        const properties = this.buildAddonProperties(
          button,
          parentItem,
          normalizedParent.configurationId,
          parentLineKey
        );
        await this.request(this.dataset.cartAddUrl || '/cart/add.js', {
          items: [{ id: variantId, quantity: 1, properties }],
        });
        const updatedCart = await this.fetchCart();
        await this.refresh(updatedCart);
      } catch (error) {
        button.removeAttribute('aria-busy');
        this.showError('We could not add this item. Please try again.');
        this.setBusy(false);
      }
    }

    buildAddonProperties(button, parentItem, configurationId, parentLineKey) {
      const properties = {};
      const samePhoto = button.dataset.samePhoto === 'true';
      if (samePhoto && parentItem?.properties) {
        [
          'Original photo', '_Original photo', 'Photo transformation', 'Photo fit'
        ].forEach((key) => {
          const value = parentItem.properties[key];
          if (value !== null && value !== undefined && value !== '') properties[key] = value;
        });
        properties['_Photo source configuration'] = configurationId;
      }
      const itemType = button.dataset.itemType || 'Add-on';
      properties['_Configuration ID'] = configurationId;
      properties['_Item type'] = itemType;
      properties['_Addon group'] = this.normalize(itemType);
      properties['_Parent line key'] = parentLineKey;
      properties['Configured with'] = button.dataset.configuredWith || parentItem?.product_title || '';
      if (samePhoto && this.normalize(itemType) === 'crystal-accessory' && button.dataset.photoProfile) {
        properties['Personalizer profile'] = button.dataset.photoProfile;
      }
      if (this.normalize(itemType) === 'light-base') {
        properties['_Compatible parent variant'] = String(parentItem.variant_id);
      }
      return properties;
    }

    async addCopy(button) {
      if (this.busy || button.disabled) return;
      const copyVariantId = Number.parseInt(button.dataset.variantId || '', 10);
      const parentLineKey = button.dataset.parentLineKey;
      if (!copyVariantId || !parentLineKey) return;
      this.setBusy(true);
      this.showError('');
      try {
        const cart = await this.fetchCart();
        let parent = cart.items.find((item) => item.key === parentLineKey);
        if (!parent) throw new Error('Parent cart item was not found');
        const normalized = await this.ensureConfigurationId(parent);
        parent = normalized.item;
        const properties = this.copyPersonalizationProperties(parent.properties || {});
        properties['_Configuration ID'] = normalized.configurationId;
        properties['_Item type'] = 'Copy';
        properties['_Parent line key'] = parent.key;
        properties['_Copy source variant'] = String(parent.variant_id);
        properties['Configured with'] = button.dataset.configurationLabel || parent.product_title || '';
        const existingCopy = cart.items.find((item) =>
          item.properties?.['_Configuration ID'] === normalized.configurationId &&
          this.normalize(item.properties?.['_Item type']) === 'copy' &&
          Number(item.variant_id) === copyVariantId
        );
        if (existingCopy) {
          await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
            id: existingCopy.key,
            quantity: Math.max(1, Number(existingCopy.quantity || 1)) + 1,
          });
        } else {
          await this.request(this.dataset.cartAddUrl || '/cart/add.js', {
            items: [{ id: copyVariantId, quantity: 1, properties }],
          });
        }
        this.queueCartNotice('Copy added at 50% off.');
        const updatedCart = await this.fetchCart();
        await this.refresh(updatedCart);
      } catch (error) {
        this.showError('We could not add the half-price Copy. Please try again.');
        this.setBusy(false);
      }
    }

    copyPersonalizationProperties(source) {
      const excluded = new Set([
        '_Item type', '_Parent line key', '_Copy source variant', '_Photo replacement token',
        '_Full edit replacement token', 'Selected add-ons', 'Light base', 'Crystal accessories',
        'Greeting card', 'Gift wrapping', 'Video book'
      ]);
      const properties = {};
      Object.entries(source || {}).forEach(([key, value]) => {
        if (!excluded.has(key) && value !== null && value !== '') properties[key] = value;
      });
      return properties;
    }

    async openFullConfiguration(link) {
      const configurationId = link.dataset.configurationId;
      if (!configurationId) {
        window.location.assign(link.href);
        return;
      }
      try {
        const cart = await this.fetchCart();
        const items = cart.items
          .filter((item) => item.properties?.['_Configuration ID'] === configurationId || item.key === configurationId)
          .map((item) => ({
            key: item.key,
            variant_id: item.variant_id,
            quantity: item.quantity,
            product_type: item.product_type,
            properties: item.properties || {}
          }));
        const main = items.find((item) => !item.properties?.['_Item type']);
        sessionStorage.setItem(FULL_EDIT_STORAGE_PREFIX + configurationId, JSON.stringify({
          configurationId,
          originalVariantId: main?.variant_id || null,
          items,
          createdAt: Date.now()
        }));
      } catch (error) {
        try {
          sessionStorage.setItem(CART_NOTICE_STORAGE, 'The editor will restore this configuration directly from your cart.');
        } catch (storageError) {
          // Navigation and cart-based restoration must work without browser storage.
        }
      } finally {
        window.location.assign(link.href);
      }
    }

    loadPhotoImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        if (/^(https?:)?\/\//i.test(src)) image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    canvasBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The effect preview could not be created.')), 'image/png', 0.94);
      });
    }

    wrapCanvasText(context, value, maxWidth) {
      return String(value || '').split('\n').flatMap((paragraph) => {
        if (!paragraph) return [''];
        const units = paragraph.includes(' ') ? paragraph.split(/\s+/) : [...paragraph];
        const separator = paragraph.includes(' ') ? ' ' : '';
        const lines = [];
        let line = '';
        units.forEach((unit) => {
          const candidate = line ? `${line}${separator}${unit}` : unit;
          if (line && context.measureText(candidate).width > maxWidth) {
            lines.push(line);
            line = unit;
          } else {
            line = candidate;
          }
        });
        if (line) lines.push(line);
        return lines;
      });
    }

    photoTransformationValue(sourceState = this.photoEditorState) {
      const state = sourceState || {};
      const fitLabel = state.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
      return `Mode ${fitLabel}, zoom ${Math.round(state.zoom || 100)}%, rotation ${Math.round(state.rotation || 0)}deg, position X ${Math.round(state.x || 0)}%, Y ${Math.round(state.y || 0)}%`;
    }

    hasPhotoEditorChanges() {
      const state = this.photoEditorState;
      if (!state) return false;
      return this.photoTransformationValue(state) !== state.initialTransformation ||
        state.layout !== state.initialLayout;
    }

    async createCartAdjustedPreviewFile(file, overrides = {}) {
      const state = { ...(this.photoEditorState || {}), ...overrides };
      if (!state?.hasComposite || !state.baseUrl || !state.maskUrl || !state.overlayUrl || !state.sourceUrl) return null;
      const [baseImage, maskImage, overlayImage, photoImage] = await Promise.all([
        this.loadPhotoImage(state.baseUrl),
        this.loadPhotoImage(state.maskUrl),
        this.loadPhotoImage(state.overlayUrl),
        this.loadPhotoImage(state.sourceUrl),
      ]);
      const size = 1200;
      const output = document.createElement('canvas');
      output.width = size;
      output.height = size;
      const outputContext = output.getContext('2d');
      outputContext.drawImage(baseImage, 0, 0, size, size);

      const processed = document.createElement('canvas');
      processed.width = size;
      processed.height = size;
      const photoContext = processed.getContext('2d');
      const baseScale = state.fit === 'fit'
        ? Math.min(size / photoImage.naturalWidth, size / photoImage.naturalHeight)
        : Math.max(size / photoImage.naturalWidth, size / photoImage.naturalHeight);
      const drawWidth = photoImage.naturalWidth * baseScale;
      const drawHeight = photoImage.naturalHeight * baseScale;
      photoContext.save();
      photoContext.translate(size / 2 + (state.x / 100) * size, size / 2 + (state.y / 100) * size);
      photoContext.rotate((state.rotation * Math.PI) / 180);
      photoContext.scale(state.zoom / 100, state.zoom / 100);
      photoContext.filter = 'grayscale(1) contrast(1.08)';
      photoContext.drawImage(photoImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
      photoContext.restore();
      photoContext.filter = 'none';

      if (state.engravingText) {
        const engravingSize = this.clamp(state.engravingSize, 14, 72) * (size / 620);
        photoContext.save();
        photoContext.font = `600 ${engravingSize}px ${state.engravingFont || 'Georgia'}`;
        photoContext.textAlign = 'center';
        photoContext.textBaseline = 'middle';
        photoContext.fillStyle = 'rgba(250,250,250,.92)';
        photoContext.strokeStyle = 'rgba(8,14,26,.72)';
        photoContext.lineWidth = Math.max(1, engravingSize * 0.035);
        const lines = this.wrapCanvasText(photoContext, state.engravingText, size * 0.62);
        const lineHeight = engravingSize * 1.16;
        const startY = size / 2 + (state.engravingY / 100) * size - ((lines.length - 1) * lineHeight) / 2;
        const textX = size / 2 + (state.engravingX / 100) * size;
        lines.forEach((line, index) => {
          const textY = startY + index * lineHeight;
          photoContext.strokeText(line, textX, textY, size * 0.62);
          photoContext.fillText(line, textX, textY, size * 0.62);
        });
        photoContext.restore();
      }

      photoContext.globalCompositeOperation = 'destination-in';
      photoContext.drawImage(maskImage, 0, 0, size, size);
      photoContext.globalCompositeOperation = 'source-over';
      outputContext.drawImage(processed, 0, 0);
      outputContext.drawImage(overlayImage, 0, 0, size, size);
      const blob = await this.canvasBlob(output);
      const sourceName = file?.name || 'existing-photo';
      const safeName = (sourceName.replace(/\.[^.]+$/, '') || 'photo').replace(/[^a-z0-9_-]+/gi, '-');
      return new File([blob], `${safeName}-${state.profile}-${state.layout}-preview.png`, { type: 'image/png' });
    }

    async savePhoto(event) {
      event.preventDefault();
      const form = event.currentTarget;
      if (this.photoEditorContext?.type === 'accessory-add' || this.photoEditorContext?.type === 'accessory-adjust') {
        await this.saveAccessoryPhoto(form);
        return;
      }
      if (this.busy) return;
      const file = form.querySelector('[data-photo-input]')?.files?.[0];
      const lineKey = form.querySelector('[data-photo-line-key]')?.value;
      const configurationId = form.querySelector('[data-photo-configuration-id]')?.value;
      const errorNode = form.querySelector('[data-photo-error]');
      if (!lineKey || !configurationId) {
        if (errorNode) errorNode.textContent = 'The configuration changed. Reopen the photo editor.';
        return;
      }
      if (!file && !this.photoEditorState?.originalUrl) {
        if (errorNode) errorNode.textContent = 'The original photo is unavailable. Choose a new photo to continue.';
        return;
      }
      if (!file && !this.hasPhotoEditorChanges()) {
        this.closeDialog('[data-photo-modal]');
        this.showToast('No photo changes were needed.');
        return;
      }

      this.setBusy(true);
      if (errorNode) errorNode.textContent = '';
      try {
        let cart = await this.fetchCart();
        const parent = cart.items.find((item) => item.key === lineKey) ||
          cart.items.find((item) =>
            item.properties?.['_Configuration ID'] === configurationId &&
            !item.properties?.['_Item type']
          );
        if (!parent) throw new Error('The configuration changed. Reopen the photo editor.');
        const mainGroup = cart.items.filter((item) =>
          item.properties?.['_Configuration ID'] === configurationId &&
          !item.properties?.['_Item type']
        );
        const mainQuantity = mainGroup.reduce((total, item) => total + Number(item.quantity || 0), 0) || parent.quantity;
        const existingPhotoUrl = parent.properties?.['Original photo'] ||
          parent.properties?.['_Original photo'] ||
          this.photoEditorState?.originalUrl || '';
        if (!file && !existingPhotoUrl) throw new Error('The original photo is unavailable. Choose a new photo to continue.');
        const token = this.createToken('photo');
        const properties = this.copyPersonalizationProperties(parent.properties || {});
        delete properties['Original photo'];
        delete properties['_Original photo'];
        delete properties['Adjusted preview'];
        delete properties['_Adjusted preview'];
        properties['_Configuration ID'] = configurationId;
        properties['_Photo replacement token'] = token;
        properties['Photo transformation'] = this.photoTransformationValue();
        properties['Photo fit'] = this.photoEditorState?.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
        properties['Preview layout'] = this.photoEditorState?.layout || 'default';
        properties['Personalizer profile'] = this.photoEditorState?.profile || properties['Personalizer profile'] || 'default';

        let adjustedPreviewFile = null;
        try {
          adjustedPreviewFile = await this.createCartAdjustedPreviewFile(file);
        } catch (previewError) {
          if (file) throw previewError;
          console.warn('[ArtLaser3D cart] Existing photo preview regeneration fell back to dynamic rendering.', previewError);
        }

        const formData = new FormData();
        formData.append('id', String(parent.variant_id));
        formData.append('quantity', String(mainQuantity));
        Object.entries(properties).forEach(([key, value]) => formData.append('properties[' + key + ']', String(value)));
        if (file) formData.append('properties[Original photo]', file, file.name);
        else formData.append('properties[Original photo]', existingPhotoUrl);
        if (adjustedPreviewFile) formData.append('properties[Adjusted preview]', adjustedPreviewFile, adjustedPreviewFile.name);
        else if (!file) formData.append('properties[Adjusted preview]', existingPhotoUrl);

        const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const added = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok || added.errors) throw new Error(added.description || 'The photo upload failed.');

        const removeMainUpdates = {};
        mainGroup.forEach((item) => { removeMainUpdates[item.key] = 0; });
        await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', { updates: removeMainUpdates });
        cart = await this.fetchCart();
        let replacement = cart.items.find((item) => item.properties?.['_Photo replacement token'] === token);
        if (!replacement) throw new Error('The replacement item was not found.');

        const photoUrl = replacement.properties?.['Original photo'] || '';
        const adjustedPreviewUrl = replacement.properties?.['Adjusted preview'] || photoUrl;
        const normalizedProperties = { ...(replacement.properties || {}) };
        delete normalizedProperties['_Photo replacement token'];
        await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
          id: replacement.key,
          quantity: replacement.quantity,
          properties: normalizedProperties
        });

        cart = await this.fetchCart();
        const normalizedMain = cart.items.find((item) =>
          item.properties?.['_Configuration ID'] === configurationId &&
          !item.properties?.['_Item type']
        );
        await this.syncPhotoToRelatedItems(configurationId, photoUrl, adjustedPreviewUrl, file, normalizedMain?.key || '');
        cart = await this.fetchCart();
        this.queueCartNotice(file
          ? 'Photo replaced. Existing add-ons were preserved.'
          : 'Photo adjustments saved. Existing add-ons were preserved.');
        await this.refresh(cart);
      } catch (error) {
        if (errorNode) errorNode.textContent = error.message || 'We could not update the photo. Please try again.';
        this.setBusy(false);
      }
    }

    async saveAccessoryPhoto(form) {
      if (this.busy) return;
      const context = this.photoEditorContext;
      const state = this.photoEditorState;
      const errorNode = form.querySelector('[data-photo-error]');
      const file = form.querySelector('[data-photo-input]')?.files?.[0];
      if (!context || !state) {
        if (errorNode) errorNode.textContent = 'Reopen the accessory photo editor and try again.';
        return;
      }
      if (!file && !state.originalUrl && !state.sourceUrl) {
        if (errorNode) errorNode.textContent = 'The main crystal photo is unavailable. Choose a new photo to continue.';
        return;
      }

      const hasCustomPhoto = Boolean(file || this.hasPhotoEditorChanges());
      if (context.type === 'accessory-adjust' && !hasCustomPhoto) {
        this.closeDialog('[data-photo-modal]');
        this.showToast('No accessory photo changes were needed.');
        return;
      }

      this.setBusy(true);
      if (errorNode) errorNode.textContent = '';
      try {
        let cart;
        let notice = '';
        if (context.type === 'accessory-add') {
          cart = await this.addAccessoryWithPhoto(context, file, hasCustomPhoto ? 'custom' : 'synced');
          notice = 'Crystal accessory added.';
        } else {
          cart = await this.fetchCart();
          const target = cart.items.find((item) => item.key === context.targetLineKey);
          if (!target) throw new Error('The accessory changed. Reopen the photo editor.');
          const sourceUrl = state.originalUrl || state.sourceUrl || this.photoUrlForItem(target);
          const updated = await this.replaceAccessoryPhotoPreview(
            target,
            file,
            sourceUrl,
            state.profile,
            {
              baseUrl: state.baseUrl,
              maskUrl: state.maskUrl,
              overlayUrl: state.overlayUrl
            },
            context.parentLineKey || target.properties?.['_Parent line key'] || '',
            'custom'
          );
          if (!updated) throw new Error('The accessory preview could not be updated.');
          cart = await this.fetchCart();
          notice = 'Accessory photo updated.';
        }

        this.queueCartNotice(notice);
        await this.refresh(cart);
      } catch (error) {
        if (errorNode) errorNode.textContent = error.message || 'We could not update this accessory. Please try again.';
        this.setBusy(false);
      }
    }

    async addAccessoryWithPhoto(context, file, photoMode) {
      const variantId = Number.parseInt(String(context.variantId || ''), 10);
      if (!variantId) throw new Error('This accessory is unavailable.');
      const token = this.createToken('accessory-add');
      try {
        let cart = await this.fetchCart();
        let parent = cart.items.find((item) => item.key === context.parentLineKey) ||
          cart.items.find((item) =>
            context.configurationId &&
            item.properties?.['_Configuration ID'] === context.configurationId &&
            !item.properties?.['_Item type']
          );
        if (!parent) throw new Error('The main crystal was not found.');

        const currentConfigurationId = this.configurationIdFor(parent) || context.configurationId || parent.key;
        const existingAccessory = cart.items.find((item) =>
          String(item.variant_id) === String(variantId) &&
          this.isAccessoryItem(item) &&
          (item.properties?.['_Configuration ID'] === currentConfigurationId || item.properties?.['_Parent line key'] === parent.key)
        );
        if (existingAccessory) return cart;

        const normalized = await this.ensureConfigurationId(parent);
        parent = normalized.item;
        const addonData = { ...(context.addonData || {}), parentLineKey: parent.key };
        const properties = this.buildAddonProperties(
          { dataset: addonData },
          parent,
          normalized.configurationId,
          parent.key
        );
        const originalUrl = file ? '' : (this.photoEditorState?.originalUrl || this.photoEditorState?.sourceUrl || this.photoUrlForItem(parent));
        if (!file && !originalUrl) throw new Error('The main crystal photo is unavailable. Choose a new photo to continue.');

        delete properties['Original photo'];
        delete properties['_Original photo'];
        delete properties['Adjusted preview'];
        delete properties['_Adjusted preview'];
        properties['_Accessory photo add token'] = token;
        properties['_Accessory photo mode'] = photoMode;
        if (photoMode === 'custom') properties['_Accessory photo override'] = 'true';
        else delete properties['_Accessory photo override'];
        properties['Personalizer profile'] = this.photoEditorState?.profile || context.profile || '';
        properties['_Accessory photo profile'] = this.photoEditorState?.profile || context.profile || '';
        properties['Photo transformation'] = this.photoTransformationValue();
        properties['Photo fit'] = this.photoEditorState?.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
        properties['Preview layout'] = 'default';

        let previewFile = null;
        try {
          previewFile = await this.createCartAdjustedPreviewFile(file);
        } catch (previewError) {
          console.warn('[ArtLaser3D cart] Accessory preview upload fell back to dynamic rendering.', previewError);
        }

        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', '1');
        Object.entries(properties).forEach(([key, value]) => formData.append('properties[' + key + ']', String(value)));
        if (file) formData.append('properties[Original photo]', file, file.name);
        else formData.append('properties[Original photo]', originalUrl);
        if (previewFile) formData.append('properties[Adjusted preview]', previewFile, previewFile.name);

        const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const added = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok || added.errors) throw new Error(added.description || 'The accessory could not be added.');

        cart = await this.fetchCart();
        const replacement = cart.items.find((item) => item.properties?.['_Accessory photo add token'] === token);
        if (!replacement) throw new Error('The added accessory was not found.');
        const normalizedProperties = { ...(replacement.properties || {}) };
        delete normalizedProperties['_Accessory photo add token'];
        normalizedProperties['_Original photo'] = normalizedProperties['Original photo'] || originalUrl;
        normalizedProperties['_Adjusted preview'] = normalizedProperties['Adjusted preview'] || '';
        await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
          id: replacement.key,
          quantity: replacement.quantity,
          properties: normalizedProperties
        });
        return await this.fetchCart();
      } catch (error) {
        try {
          const cleanupCart = await this.fetchCart();
          const pending = cleanupCart.items.find((item) => item.properties?.['_Accessory photo add token'] === token);
          if (pending) {
            await this.request(this.dataset.cartChangeUrl || '/cart/change.js', { id: pending.key, quantity: 0 });
          }
        } catch (cleanupError) {
          console.warn('[ArtLaser3D cart] Pending accessory cleanup failed.', cleanupError);
        }
        throw error;
      }
    }

    async replaceAccessoryPhotoPreview(target, file, photoUrl, profile, assets, parentLineKey, photoMode = 'custom') {
      if (!target || (!file && !photoUrl) || !profile || !assets?.baseUrl || !assets?.maskUrl || !assets?.overlayUrl) return false;
      const previewFile = await this.createCartAdjustedPreviewFile(file, {
        hasComposite: true,
        profile,
        mode: 'fixed',
        layout: 'default',
        baseUrl: assets.baseUrl,
        maskUrl: assets.maskUrl,
        overlayUrl: assets.overlayUrl,
      });
      if (!previewFile) return false;

      const token = this.createToken('accessory-photo');
      const properties = { ...(target.properties || {}) };
      delete properties['Original photo'];
      delete properties['_Original photo'];
      delete properties['Adjusted preview'];
      delete properties['_Adjusted preview'];
      properties['_Accessory photo replacement token'] = token;
      properties['_Accessory photo mode'] = photoMode;
      if (photoMode === 'custom') properties['_Accessory photo override'] = 'true';
      else delete properties['_Accessory photo override'];
      properties['Personalizer profile'] = profile;
      properties['_Accessory photo profile'] = profile;
      properties['Photo transformation'] = this.photoTransformationValue();
      properties['Photo fit'] = this.photoEditorState?.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
      properties['Preview layout'] = 'default';
      if (parentLineKey) properties['_Parent line key'] = parentLineKey;

      const formData = new FormData();
      formData.append('id', String(target.variant_id));
      formData.append('quantity', String(target.quantity));
      if (target.selling_plan_allocation?.selling_plan?.id) {
        formData.append('selling_plan', String(target.selling_plan_allocation.selling_plan.id));
      }
      Object.entries(properties).forEach(([key, value]) => formData.append(`properties[${key}]`, String(value)));
      if (file) formData.append('properties[Original photo]', file, file.name);
      else formData.append('properties[Original photo]', photoUrl);
      formData.append('properties[Adjusted preview]', previewFile, previewFile.name);

      const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const added = await addResponse.json().catch(() => ({}));
      if (!addResponse.ok || added.errors) throw new Error(added.description || 'The accessory preview upload failed.');

      let cart = await this.fetchCart();
      let replacement = cart.items.find((item) => item.properties?.['_Accessory photo replacement token'] === token);
      if (!replacement) throw new Error('The replacement accessory was not found.');

      try {
        await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', { updates: { [target.key]: 0 } });
      } catch (removeError) {
        try {
          await this.request(this.dataset.cartChangeUrl || '/cart/change.js', { id: replacement.key, quantity: 0 });
        } catch (cleanupError) {
          console.warn('[ArtLaser3D cart] Accessory replacement rollback failed.', cleanupError);
        }
        throw removeError;
      }

      cart = await this.fetchCart();
      replacement = cart.items.find((item) => item.properties?.['_Accessory photo replacement token'] === token);
      if (!replacement) throw new Error('The replacement accessory was lost after the cart update.');
      const normalizedProperties = { ...(replacement.properties || {}) };
      delete normalizedProperties['_Accessory photo replacement token'];
      normalizedProperties['_Original photo'] = normalizedProperties['Original photo'] || photoUrl;
      normalizedProperties['_Adjusted preview'] = normalizedProperties['Adjusted preview'] || '';
      try {
        await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
          id: replacement.key,
          quantity: replacement.quantity,
          properties: normalizedProperties
        });
      } catch (normalizeError) {
        console.warn('[ArtLaser3D cart] Accessory preview was saved but its temporary marker could not be cleared.', normalizeError);
      }
      return true;
    }

    async syncPhotoToRelatedItems(configurationId, photoUrl, adjustedPreviewUrl, file, parentLineKey) {
      if (!photoUrl) return;
      let cart = await this.fetchCart();
      const targets = cart.items
        .filter((item) =>
          item.properties?.['_Configuration ID'] === configurationId &&
          this.isPhotoSyncItem(item)
        )
        .map((item) => item.key);

      for (const originalKey of targets) {
        cart = await this.fetchCart();
        const target = cart.items.find((item) => item.key === originalKey);
        if (!target) continue;
        const properties = { ...(target.properties || {}) };
        const itemType = this.normalize(target.properties?.['_Item type']);
        const isAccessory = this.isAccessoryItem(target);

        if (isAccessory && this.isCustomAccessoryPhoto(target)) {
          if (parentLineKey && properties['_Parent line key'] !== parentLineKey) {
            properties['_Parent line key'] = parentLineKey;
            await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
              id: target.key,
              quantity: target.quantity,
              properties
            });
          }
          continue;
        }

        if (isAccessory) {
          const profile = this.accessoryProfileForItem(target);
          const assets = this.photoEditorState?.accessoryAssets?.[profile] || this.accessoryAssetMap()[profile];
          try {
            if (await this.replaceAccessoryPhotoPreview(target, file, photoUrl, profile, assets, parentLineKey, 'synced')) continue;
          } catch (error) {
            console.warn('[ArtLaser3D cart] Accessory preview upload fell back to dynamic rendering.', error);
          }
          if (profile) {
            properties['Personalizer profile'] = profile;
            properties['_Accessory photo profile'] = profile;
          }
          properties['Preview layout'] = 'default';
          properties['Photo transformation'] = this.photoTransformationValue();
          properties['Photo fit'] = this.photoEditorState?.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
          properties['_Accessory photo mode'] = 'synced';
          delete properties['_Accessory photo override'];
          properties['Original photo'] = photoUrl;
          properties['_Original photo'] = photoUrl;
          delete properties['Adjusted preview'];
          delete properties['_Adjusted preview'];
        } else {
          properties['Original photo'] = photoUrl;
          properties['_Original photo'] = photoUrl;
          properties['Adjusted preview'] = adjustedPreviewUrl || photoUrl;
          properties['_Adjusted preview'] = adjustedPreviewUrl || photoUrl;
          if (itemType === 'copy') {
            properties['Photo transformation'] = this.photoTransformationValue();
            properties['Photo fit'] = this.photoEditorState?.fit === 'fit' ? 'Fit entire photo' : 'Fill shape';
            properties['Preview layout'] = this.photoEditorState?.layout || properties['Preview layout'] || 'default';
            properties['Personalizer profile'] = this.photoEditorState?.profile || properties['Personalizer profile'] || 'default';
          }
        }
        if (parentLineKey) properties['_Parent line key'] = parentLineKey;
        await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
          id: target.key,
          quantity: target.quantity,
          properties
        });
      }
    }

    async ensureConfigurationId(parentItem) {
      const existingId = parentItem.properties?.['_Configuration ID'];
      if (existingId) return { item: parentItem, cart: null, configurationId: existingId };
      const generatedId = this.createToken('ag');
      const properties = { ...(parentItem.properties || {}), '_Configuration ID': generatedId };
      await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
        id: parentItem.key,
        quantity: parentItem.quantity,
        properties
      });
      const cart = await this.fetchCart();
      const updatedItem = cart.items.find((item) => item.properties?.['_Configuration ID'] === generatedId);
      if (!updatedItem) throw new Error('Updated parent cart item was not found');
      return { item: updatedItem, cart, configurationId: generatedId };
    }

    async fetchCart() {
      const cartUrl = this.dataset.cartUrl || '/cart';
      const response = await fetch(cartUrl + '.js', {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.items)) throw new Error('Cart request failed');
      return data;
    }

    async updateConfigurationQuantity(configuration, quantity) {
      if (this.busy) return;
      const configurationId = configuration.dataset.configuration;
      if (!configurationId) {
        await this.updateLine(configuration.dataset.mainLineKey, quantity);
        return;
      }
      this.setBusy(true);
      this.showError('');
      try {
        const cart = await this.fetchCart();
        const seedItem = cart.items.find((item) => item.key === configurationId);
        const resolvedConfigurationId = this.configurationIdFor(seedItem) || configurationId;
        const groupItems = cart.items.filter((item) => this.configurationIdFor(item) === resolvedConfigurationId);
        const mainItems = groupItems.filter((item) => !item.properties?.['_Item type'] && !item.properties?.['_Parent variant ID']);
        const main = mainItems[0];
        if (!main) throw new Error('Main crystal was not found');
        if (quantity === 0) {
          await this.removeConfigurationById(configurationId, cart);
          return;
        }
        const updates = {};
        updates[main.key] = quantity;
        mainItems.slice(1).forEach((item) => { updates[item.key] = 0; });
        groupItems.forEach((item) => {
          if (item.key === main.key) return;
          const type = this.normalize(item.properties?.['_Item type']);
          const isIndependentAddon = Boolean(type && type !== 'copy' && type !== 'free-gift');
          if (isIndependentAddon) return;
          const multiplier = Math.max(1, Number.parseInt(item.properties?.['_Quantity multiplier'] || '1', 10));
          if (item.properties?.['_Sync quantity'] === 'true' || item.properties?.['_Quantity multiplier']) {
            updates[item.key] = quantity * multiplier;
          }
        });
        const updated = await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', { updates });
        await this.refresh(updated);
      } catch (error) {
        this.showError('We could not synchronize this configuration. Please try again.');
        this.setBusy(false);
      }
    }

    async updateLine(lineKey, quantity) {
      if (!lineKey || this.busy) return false;
      this.setBusy(true);
      this.showError('');
      try {
        const cart = await this.request(this.dataset.cartChangeUrl || '/cart/change.js', { id: lineKey, quantity });
        await this.refresh(cart);
        return true;
      } catch (error) {
        this.showError('We could not update this item. Please try again.');
        this.setBusy(false);
        return false;
      }
    }

    async removeConfigurationById(configurationId, existingCart) {
      if (!configurationId || this.busy && !existingCart) return;
      if (!existingCart) this.setBusy(true);
      this.showError('');
      try {
        const cart = existingCart || await this.fetchCart();
        const updates = {};
        const seedItem = cart.items.find((item) => item.key === configurationId);
        const isLegacyAddon = Boolean(
          seedItem?.properties?.['_Parent variant ID'] &&
          !seedItem?.properties?.['_Configuration ID']
        );
        if (isLegacyAddon) {
          updates[seedItem.key] = 0;
        } else {
          const resolvedConfigurationId = this.configurationIdFor(seedItem) || configurationId;
          cart.items
            .filter((item) => this.configurationIdFor(item) === resolvedConfigurationId)
            .forEach((item) => { updates[item.key] = 0; });
        }
        if (!Object.keys(updates).length) throw new Error('Configuration was not found');
        const updated = await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', { updates });
        await this.refresh(updated);
      } catch (error) {
        this.showError('We could not remove this configuration. Please try again.');
        this.setBusy(false);
      }
    }

    applyDiscount(event) {
      event.preventDefault();
      const input = event.currentTarget.querySelector('[data-discount-input]');
      const code = input?.value.trim();
      if (!code) {
        input?.focus();
        this.showError('Enter a discount code to apply it.');
        return;
      }
      this.applyPromotion(code, code, event.currentTarget.querySelector('button'));
    }

    async applyPromotion(code, title, button) {
      if (!code || this.busy) return;
      this.setBusy(true);
      this.showError('');
      button?.setAttribute('aria-busy', 'true');
      try {
        const before = await this.fetchCart();
        const activeCodes = this.getActiveDiscountCodes(before);
        const normalizedCode = code.trim();
        if (activeCodes.some((active) => active.toLowerCase() === normalizedCode.toLowerCase())) {
          this.closeDialog('[data-promotions-modal]');
          this.showToast('This promotion is already applied.');
          this.setBusy(false);
          return;
        }

        const desiredCodes = [...activeCodes, normalizedCode];
        const after = await this.updateDiscountCodes(desiredCodes, before, title);
        const verification = this.verifyDiscountCodes(after, desiredCodes, before);
        if (activeCodes.length && !verification.allApplicable) {
          await this.updateDiscountCodes(activeCodes, before, '', true);
          this.pendingConflict = { code: normalizedCode, title: title || normalizedCode };
          const titleNode = this.querySelector('[data-conflict-new-title]');
          if (titleNode) titleNode.textContent = this.pendingConflict.title;
          this.closeDialog('[data-promotions-modal]');
          this.openDialog('[data-conflict-modal]');
          this.setBusy(false);
          return;
        }

        if (!verification.newApplicable) {
          await this.updateDiscountCodes(activeCodes, before, '', true);
          throw new Error('This promotion is not eligible for the current cart.');
        }

        this.closeDialog('[data-promotions-modal]');
        this.showToast(activeCodes.length ? 'Promotions combined successfully.' : 'Promotion applied.');
        await this.refresh(after);
      } catch (error) {
        button?.removeAttribute('aria-busy');
        this.showError(error.message || 'We could not apply this promotion.');
        this.setBusy(false);
      }
    }

    async replacePromotion() {
      if (!this.pendingConflict || this.busy) return;
      const pending = this.pendingConflict;
      this.setBusy(true);
      try {
        const before = await this.fetchCart();
        const previousCodes = this.getActiveDiscountCodes(before);
        const previousTitles = this.getStoredPromotionTitles(before);
        const cleared = await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', {
          discount: '',
          attributes: {
            [PROMOTION_CODES_ATTRIBUTE]: '',
            [PROMOTION_TITLES_ATTRIBUTE]: ''
          }
        });
        const after = await this.updateDiscountCodes([pending.code], cleared, pending.title, true, true);
        const verification = this.verifyDiscountCodes(after, [pending.code], cleared);
        if (!verification.newApplicable) {
          await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', {
            discount: '',
            attributes: {
              [PROMOTION_CODES_ATTRIBUTE]: '',
              [PROMOTION_TITLES_ATTRIBUTE]: ''
            }
          });
          if (previousCodes.length) {
            await this.updateDiscountCodes(previousCodes, before, previousTitles.join(' + '), true, true);
          }
          throw new Error('This promotion is not eligible for the current cart. Your previous promotion was restored.');
        }
        this.pendingConflict = null;
        this.closeDialog('[data-conflict-modal]');
        this.showToast('Promotion switched to ' + pending.title + '.');
        await this.refresh(after);
      } catch (error) {
        this.showError(error.message || 'We could not switch promotions.');
        this.setBusy(false);
      }
    }

    async removePromotions() {
      if (this.busy) return;
      this.setBusy(true);
      try {
        const cart = await this.fetchCart();
        const updated = await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', {
          discount: '',
          attributes: {
            [PROMOTION_CODES_ATTRIBUTE]: '',
            [PROMOTION_TITLES_ATTRIBUTE]: ''
          }
        });
        this.showToast('Promotion codes removed.');
        await this.refresh(updated || cart);
      } catch (error) {
        this.showError('We could not remove the promotion codes.');
        this.setBusy(false);
      }
    }

    async updateDiscountCodes(codes, cart, title, saveAttributes = true, replaceTitles = false) {
      const cleanCodes = [...new Set(codes.map((code) => String(code || '').trim()).filter(Boolean))];
      const existingTitles = replaceTitles ? [] : this.getStoredPromotionTitles(cart);
      const titles = [...existingTitles];
      if (title && !titles.some((value) => value.toLowerCase() === String(title).toLowerCase())) titles.push(title);
      const payload = { discount: cleanCodes.join(',') };
      if (saveAttributes) {
        payload.attributes = {
          [PROMOTION_CODES_ATTRIBUTE]: cleanCodes.join(', '),
          [PROMOTION_TITLES_ATTRIBUTE]: titles.join(' + ')
        };
      }
      return this.request(this.dataset.cartUpdateUrl || '/cart/update.js', payload);
    }

    getActiveDiscountCodes(cart) {
      const stored = String(cart.attributes?.[PROMOTION_CODES_ATTRIBUTE] || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
      const apiCodes = (cart.discount_codes || cart.discountCodes || [])
        .filter((entry) => entry && entry.applicable !== false)
        .map((entry) => entry.code)
        .filter(Boolean);
      return [...new Set([...stored, ...apiCodes])];
    }

    getStoredPromotionTitles(cart) {
      return String(cart.attributes?.[PROMOTION_TITLES_ATTRIBUTE] || '')
        .split(/\s+\+\s+/)
        .map((title) => title.trim())
        .filter(Boolean);
    }

    discountEvidence(cart) {
      const titles = [];
      (cart.cart_level_discount_applications || []).forEach((application) => {
        if (application?.title) titles.push(application.title);
      });
      (cart.items || []).forEach((item) => {
        (item.discount_allocations || []).forEach((allocation) => {
          const title = allocation?.discount_application?.title;
          if (title) titles.push(title);
        });
      });
      const codeEntries = cart.discount_codes || cart.discountCodes || [];
      return {
        titles: titles.map((value) => String(value).toLowerCase()),
        codeEntries: codeEntries.map((entry) => ({
          code: String(entry?.code || '').toLowerCase(),
          applicable: entry?.applicable !== false
        })),
        totalDiscount: Number(cart.total_discount || 0),
        totalPrice: Number(cart.total_price || 0)
      };
    }

    verifyDiscountCodes(after, desiredCodes, before) {
      const evidence = this.discountEvidence(after);
      const beforeEvidence = this.discountEvidence(before);
      const normalized = desiredCodes.map((code) => code.toLowerCase());
      if (evidence.codeEntries.length) {
        const applicable = normalized.filter((code) =>
          evidence.codeEntries.some((entry) => entry.code === code && entry.applicable)
        );
        return {
          allApplicable: applicable.length === normalized.length,
          newApplicable: applicable.includes(normalized[normalized.length - 1])
        };
      }
      const matched = normalized.filter((code) =>
        evidence.titles.some((title) => title === code || title.includes(code))
      );
      const changed = evidence.totalDiscount > beforeEvidence.totalDiscount ||
        evidence.totalPrice < beforeEvidence.totalPrice ||
        evidence.titles.length > beforeEvidence.titles.length;
      const newApplicable = matched.includes(normalized[normalized.length - 1]) || changed;
      const currentStillVisible = normalized.slice(0, -1).every((code) =>
        evidence.titles.some((title) => title === code || title.includes(code))
      );
      return {
        allApplicable: normalized.length <= 1 ? newApplicable : newApplicable && currentStillVisible,
        newApplicable
      };
    }

    async reconcileConfigurationState() {
      if (this.reconciling || this.busy || !this.querySelector('[data-configuration]')) return;
      this.reconciling = true;
      try {
        let cart = await this.fetchCart();
        const incompatibleKeys = [];
        let copyRepair = null;
        for (const article of this.querySelectorAll('[data-configuration]')) {
          const configurationId = article.dataset.configuration;
          const main = cart.items.find((item) =>
            item.properties?.['_Configuration ID'] === configurationId &&
            !item.properties?.['_Item type']
          );
          if (!main) continue;
          const copies = cart.items.filter((item) =>
            item.properties?.['_Configuration ID'] === configurationId &&
            this.normalize(item.properties?.['_Item type']) === 'copy'
          );
          const copy = copies[0];
          const mappedCopyVariantId = Number.parseInt(article.dataset.copyVariantId || '', 10);
          if (
            !copyRepair &&
            copy &&
            mappedCopyVariantId &&
            (
              String(copy.properties?.['_Copy source variant'] || '') !== String(main.variant_id) ||
              String(copy.properties?.['_Configuration revision'] || '') !== String(main.properties?.['_Configuration revision'] || '')
            )
          ) {
            copyRepair = { main, copy, mappedCopyVariantId, configurationId };
          }
          cart.items.forEach((item) => {
            if (item.properties?.['_Configuration ID'] !== configurationId) return;
            if (this.normalize(item.properties?.['_Item type']) !== 'light-base') return;
            const compatibleVariant = item.properties?.['_Compatible parent variant'];
            if (compatibleVariant && String(compatibleVariant) !== String(main.variant_id)) incompatibleKeys.push(item.key);
          });
        }
        if (copyRepair) {
          cart = await this.replaceCopyItem(copyRepair);
          sessionStorage.setItem(CART_NOTICE_STORAGE, 'The half-price Copies were synchronized with the updated configuration.');
          await this.refresh(cart);
          return;
        }
        if (incompatibleKeys.length) {
          const updates = {};
          incompatibleKeys.forEach((key) => { updates[key] = 0; });
          cart = await this.request(this.dataset.cartUpdateUrl || '/cart/update.js', { updates });
          sessionStorage.setItem(CART_NOTICE_STORAGE, 'A light base was removed after the size changed. Please select a compatible base.');
          await this.refresh(cart);
          return;
        }
      } catch (error) {
        // Reconciliation is protective; the normal cart remains usable if it cannot run.
      } finally {
        this.reconciling = false;
      }
    }

    async replaceCopyItem({ main, copy, mappedCopyVariantId, configurationId }) {
      const copyQuantity = Math.max(1, Number(copy.quantity || 1));
      const token = this.createToken('copy-replacement');
      const properties = this.copyPersonalizationProperties(main.properties || {});
      properties['_Configuration ID'] = configurationId;
      properties['_Item type'] = 'Copy';
      properties['_Parent line key'] = main.key;
      properties['_Copy source variant'] = String(main.variant_id);
      properties['_Copy replacement token'] = token;
      properties['Configured with'] = 'Updated configuration';
      await this.request(this.dataset.cartAddUrl || '/cart/add.js', {
        items: [{ id: mappedCopyVariantId, quantity: copyQuantity, properties }]
      });
      let cart = await this.fetchCart();
      const currentOldCopy = cart.items.find((item) =>
        item.properties?.['_Configuration ID'] === configurationId &&
        this.normalize(item.properties?.['_Item type']) === 'copy' &&
        item.properties?.['_Copy replacement token'] !== token
      );
      if (currentOldCopy) {
        await this.request(this.dataset.cartChangeUrl || '/cart/change.js', { id: currentOldCopy.key, quantity: 0 });
        cart = await this.fetchCart();
      }
      const replacement = cart.items.find((item) => item.properties?.['_Copy replacement token'] === token);
      if (!replacement) throw new Error('Copy replacement was not found');
      const normalized = { ...(replacement.properties || {}) };
      delete normalized['_Copy replacement token'];
      await this.request(this.dataset.cartChangeUrl || '/cart/change.js', {
        id: replacement.key,
        quantity: copyQuantity,
        properties: normalized
      });
      cart = await this.fetchCart();
      return cart;
    }

    async request(url, payload) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Cart request failed');
      return data;
    }

    async refresh(cart) {
      const sectionId = this.dataset.sectionId;
      const cartUrl = this.dataset.cartUrl || '/cart';
      const response = await fetch(cartUrl + '?section_id=' + encodeURIComponent(sectionId), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Cart section refresh failed');
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector('[data-ag-cart-root]');
      if (!replacement) throw new Error('Updated cart section was not found');
      this.updateHeaderCount(cart);
      this.replaceWith(replacement);
      this.announceUpdate();
    }

    updateHeaderCount(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubble) {
        bubble.hidden = cart.item_count === 0;
        const visibleCount = bubble.querySelector('[aria-hidden="true"]');
        const accessibleCount = bubble.querySelector('.visually-hidden');
        if (visibleCount) visibleCount.textContent = String(cart.item_count);
        if (accessibleCount) accessibleCount.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'ag-cart-phase2' } }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'ag-cart-phase2', cartData: cart });
      }
    }

    announceUpdate() {
      const region = document.querySelector('[data-ag-cart-announcer]');
      if (region) region.textContent = 'Cart updated.';
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-loading', busy);
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-photo-modal] button, [data-photo-modal] input, [data-photo-modal] select').forEach((control) => {
        control.disabled = busy;
      });
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (error) error.textContent = message;
    }

    queueCartNotice(message) {
      if (!message) return;
      try {
        sessionStorage.setItem(CART_NOTICE_STORAGE, message);
      } catch (error) {
        // A cart update must not depend on browser storage.
      }
    }

    showToast(message) {
      const toast = this.querySelector('[data-cart-toast]');
      if (!toast || !message) return;
      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    }
  }

  customElements.define('ag-cart-page', ArtLaserCartPage);
})();
